import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  NAME_DISPLAYS,
  type NameDisplay,
  TOKEN_SIZE_PRESET_FACTOR,
  TOKEN_SIZE_PRESETS,
  type TokenSizePreset,
} from "./scene.js";

interface BoardSettingsDialogProps {
  /** Initial / currently active default token size for this board. */
  tokenSizePreset: TokenSizePreset;
  /** Initial / currently active player-name display mode. */
  nameDisplay: NameDisplay;
  /** Called when the user clicks "Übernehmen". */
  onConfirm: (next: { tokenSizePreset: TokenSizePreset; nameDisplay: NameDisplay }) => void;
  onClose: () => void;
}

const NAME_DISPLAY_META: Record<NameDisplay, { label: string; example: string; hint: string }> = {
  full: {
    label: "Voller Name",
    example: "Enes Yaman",
    hint: "Vor- und Nachname vollständig unter dem Token.",
  },
  short: {
    label: "Abgekürzt",
    example: "E. Yaman",
    hint: "Vorname als Initiale, Nachname ausgeschrieben — spart Platz.",
  },
};

const PRESET_META: Record<TokenSizePreset, { label: string; hint: string }> = {
  small: {
    label: "Klein",
    hint: "Mehr Platz auf dem Feld — gut für volle Aufstellungen.",
  },
  normal: {
    label: "Normal",
    hint: "Aktuelle Standardgröße — empfohlen für die meisten Spielzüge.",
  },
  large: {
    label: "Groß",
    hint: "Bessere Sichtbarkeit für Trainerbesprechungen am Beamer / TV.",
  },
  xl: {
    label: "XL",
    hint: "Maximale Sichtbarkeit — für kleine Aufstellungen und große Bildschirme.",
  },
};

/**
 * Per-board settings (Phase 5):
 *   • Default token size — applies to every player / dummy / opponent / ball
 *     on the board. Individual tokens can still be resized via the
 *     Transformer-Handles on top of this default.
 *
 * Designed as a LG (560 px) variant of the existing dialog look
 * (`glass-floating`, dark backdrop, lime accent) so it feels at home
 * alongside `<LabelDialog>` and `<ConfirmDialog>`.
 */
export function BoardSettingsDialog({
  tokenSizePreset,
  nameDisplay,
  onConfirm,
  onClose,
}: BoardSettingsDialogProps) {
  const [preset, setPreset] = useState<TokenSizePreset>(tokenSizePreset);
  const [name, setName] = useState<NameDisplay>(nameDisplay);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = () => onConfirm({ tokenSizePreset: preset, nameDisplay: name });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Brett-Einstellungen"
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-floating w-[min(560px,calc(100vw-32px))] rounded-3xl p-5 shadow-2xl">
        <header className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-body-md font-semibold uppercase tracking-wider">
              Brett-Einstellungen
            </h2>
            <p className="mt-0.5 text-[11px] uppercase tracking-widest text-muted-foreground">
              Gelten nur für dieses Brett · werden mit gespeichert
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
          >
            <X className="size-4" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex flex-col gap-5"
        >
          <section className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-body-sm font-semibold">Standard-Tokengröße</h3>
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Gilt für alle neuen & bestehenden Tokens
              </span>
            </div>
            <p className="text-body-sm text-muted-foreground">
              Wählt eine Basisgröße für Spieler-, Dummy-, Gegner- und Ball-Tokens. Einzelne Tokens
              lassen sich weiterhin über die Anfasser am ausgewählten Token feinjustieren.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {TOKEN_SIZE_PRESETS.map((p) => (
                <PresetCard key={p} preset={p} active={p === preset} onPick={() => setPreset(p)} />
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-body-sm font-semibold">Anzeige</h3>
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Namen unter den Spielern
              </span>
            </div>
            <p className="text-body-sm text-muted-foreground">
              Legt fest, wie Spielernamen auf dem Feld beschriftet werden.
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {NAME_DISPLAYS.map((mode) => (
                <NameDisplayCard
                  key={mode}
                  mode={mode}
                  active={mode === name}
                  onPick={() => setName(mode)}
                />
              ))}
            </div>
          </section>

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm hover:bg-white/10"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="inline-flex h-10 items-center gap-1.5 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110"
            >
              <Check className="size-4" />
              Übernehmen
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/* ─── preset card ─────────────────────────────────────────── */

interface PresetCardProps {
  preset: TokenSizePreset;
  active: boolean;
  onPick: () => void;
}

function PresetCard({ preset, active, onPick }: PresetCardProps) {
  const factor = TOKEN_SIZE_PRESET_FACTOR[preset];
  const { label, hint } = PRESET_META[preset];
  // Preview disc — visual diameter scales with the preset factor.
  const baseDiameter = 36;
  const diameter = Math.round(baseDiameter * factor);
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={[
        "group relative flex flex-col items-center gap-3 rounded-2xl border p-4 text-center transition-colors",
        active
          ? "border-pitch-lime/70 bg-pitch-lime/10 shadow-glow"
          : "border-white/15 bg-white/4 hover:border-white/30 hover:bg-white/6",
      ].join(" ")}
    >
      <span aria-hidden="true" className="flex h-16 items-center justify-center">
        <span
          className="rounded-full border-2 border-white/40 bg-pitch-lime/80 shadow-inner"
          style={{ width: diameter, height: diameter }}
        />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-body-sm font-semibold">{label}</span>
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
          ×{factor.toFixed(2).replace(/\.?0+$/, "")}
        </span>
      </span>
      <span className="text-[11px] leading-snug text-muted-foreground">{hint}</span>
      {active && (
        <span className="pointer-events-none absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-pitch-lime text-[#1d3700]">
          <Check className="size-3" />
        </span>
      )}
    </button>
  );
}

/* ─── name-display card ───────────────────────────────────── */

interface NameDisplayCardProps {
  mode: NameDisplay;
  active: boolean;
  onPick: () => void;
}

function NameDisplayCard({ mode, active, onPick }: NameDisplayCardProps) {
  const { label, example, hint } = NAME_DISPLAY_META[mode];
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={[
        "group relative flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors",
        active
          ? "border-pitch-lime/70 bg-pitch-lime/10 shadow-glow"
          : "border-white/15 bg-white/4 hover:border-white/30 hover:bg-white/6",
      ].join(" ")}
    >
      <span className="text-body-sm font-semibold">{label}</span>
      <span className="rounded-lg bg-black/25 px-2 py-1 text-[13px] font-semibold tracking-wide">
        {example}
      </span>
      <span className="text-[11px] leading-snug text-muted-foreground">{hint}</span>
      {active && (
        <span className="pointer-events-none absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-pitch-lime text-[#1d3700]">
          <Check className="size-3" />
        </span>
      )}
    </button>
  );
}
